import {
  CheckCircleIcon,
  RefreshCwIcon,
  Trash2Icon,
  XCircleIcon,
} from "lucide-react";

import { Button } from "@/components/button";
import { FILE_ICONS } from "@/features/my-files/assets/file-icons";

import type { ManagedFile } from "../../hooks/useFileUpload";

interface UploadedFilesCollectionProps {
  files: ManagedFile[];
  onClearAll?: () => void;
  onRemoveFile?: (fileId: string) => void;
  onRetryUpload?: (fileId: string) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  );
};

function getFileIcon(fileName: string) {
  const extension = fileName.split(".").pop()?.toLowerCase();

  if (extension && extension in FILE_ICONS) {
    const IconComponent = FILE_ICONS[extension as keyof typeof FILE_ICONS];

    return <IconComponent className="w-8 h-8" />;
  }

  const FallbackIcon = FILE_ICONS.fallback;

  return <FallbackIcon className="w-8 h-8" />;
}

export function UploadedFilesCollection({
  files,
  onClearAll,
  onRemoveFile,
  onRetryUpload,
}: UploadedFilesCollectionProps) {
  if (files.length === 0) {
    return null;
  }

  const hasFailedUploads = files.some((f) => f.status === "error");

  return (
    <div className="flex flex-col py-0 mb-2">
      {/* Header with actions */}
      <div className="flex items-center justify-end gap-2">
        {hasFailedUploads && onRetryUpload && (
          <Button
            className="text-xs"
            onPress={() => {
              for (const file of files) {
                if (file.status === "error") {
                  onRetryUpload(file.id);
                }
              }
            }}
            size="sm"
            variant="secondary"
          >
            Retry All Failed
          </Button>
        )}

        {files.length > 0 && onClearAll && (
          <Button
            className="text-sm font-semibold text-red-700 hover:opacity-40 py-1"
            onPress={onClearAll}
            variant="plain"
          >
            Delete all
          </Button>
        )}
      </div>

      {/* Files horizontal scroll */}
      <div className="flex @container gap-2 overflow-x-auto pb-2">
        {files.map((file) => (
          <div
            className={`
              relative group rounded-lg border border-[#EAECF0] p-2 transition-all duration-300 ease-in-out bg-white
              min-w-[120px] max-w-[120px] h-[120px] flex flex-col
            `}
            key={file.id}
          >
            {/* File Preview */}
            <div className="flex-1 flex items-center justify-center">
              {getFileIcon(file.file.name)}
            </div>

            {/* File Info */}
            <div className="flex-1 flex flex-col justify-between">
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium truncate text-black">
                  {file.file.name}
                </p>
                <span className="text-xs text-gray-500">
                  {formatFileSize(file.file.size)}
                </span>
              </div>

              {/* Status and Progress */}
              <div className="flex flex-col gap-1">
                {file.status === "uploading" && (
                  <div className="w-full bg-bg-tertiary rounded-full h-1">
                    <div
                      className="bg-utility-brand-600 h-1 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-xs text-black">
                    {file.status === "uploading" &&
                      `${Math.round(file.progress)}%`}
                    {file.status === "success" && "Complete"}
                    {file.status === "error" && "Failed"}
                    {file.status === "pending" && "Pending"}
                  </span>

                  <div className="flex items-center gap-1">
                    {file.status === "uploading" && (
                      <RefreshCwIcon className="w-3 h-3 text-utility-brand-600 animate-spin" />
                    )}
                    {file.status === "success" && (
                      <CheckCircleIcon className="w-3 h-3 text-green-600" />
                    )}
                    {file.status === "error" && (
                      <XCircleIcon className="w-3 h-3 text-red-600" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions - Show on hover */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center gap-2">
              {file.status === "error" && onRetryUpload && (
                <Button
                  className="w-8 h-8 bg-white"
                  onPress={() => onRetryUpload(file.id)}
                  size="sm"
                  variant="secondary"
                >
                  <RefreshCwIcon className="w-3 h-3" />
                </Button>
              )}

              {onRemoveFile && (
                <Button
                  className="bg-white"
                  onPress={() => onRemoveFile(file.id)}
                  size="sm"
                  variant="secondary"
                >
                  <Trash2Icon className="w-8 h-8 text-red-700" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
